import { z } from "zod";
import { clinicalPhaseSchema } from "./clinical-phase";
import { patientGroupSchema } from "./patient-group";

// TES-1070 (TES-1069 S0, point 14) — the ONE treatment-start state machine.
//
// Emelie's final decision, 2026-10-02:
// "MEDICAL_DECISION_APPROVED → AWAITING_SUBSCRIPTION → SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION → PRESCRIPTION_READY → TREATMENT_START_READY"
//
// Before this there were four parallel axes (Treatment.state,
// PrescriptionCycle.state, JourneyState and admin's clinicalStage), and none
// of them had these five states. This is a pure derivation over facts the
// SERVER has stored. Its input type is the whole list of what may move the
// state, and point 14 is the list of what may not:
// "The client never derives prescription status from a positive decision, a planned prescription, a payment redirect, a journal draft, a subscription link, form data or a prescription proposal."
// None of those is an input, so none of them can be passed by mistake.
//
// The absolute block (TES-1069): nothing past
// SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION is reachable without the final
// transaction "Bekräfta recept och publicera behandlingen" (TES-1070 S1),
// which the server refuses without a verified subscription. The derivation
// trusts that stored confirmation rather than re-checking the order of the
// writes: a patient activated by a path that existed before TES-1069 keeps
// the state she has (Simon 2026-10-02: "patients already active stay as they
// are"), so the derivation must not move her backwards.
//
// No behaviour change on its own (the TES-937 pattern). S1's admin button is
// the first reader.
//
// TES-1150 (Emelie 2026-10-07, after the first patient) — the order changed.
// The doctor now finishes the whole medical case in one session BEFORE the
// subscription is activated: journal, decision, ordination and the
// prescription ("Nytt recept är utfärdat i Webdoc" or "Befintligt recept
// gäller fortfarande"), confirmed in admin. That confirmation REGISTERS the
// prescription on the pending first cycle and nothing else. The verified
// subscription then RELEASES it (treatment-release.ts in the api): the
// cycle goes active, the pickup task, CP-12 and the "treatment ready" push
// follow. So the chain is now
//   MEDICAL_DECISION_APPROVED → AWAITING_SUBSCRIPTION
//     → PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION → PRESCRIPTION_READY
//     → TREATMENT_START_READY
// and SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION is only the side branch
// of a patient who paid before the doctor's session was finished (an
// interrupted session, or a patient who paid under the old order).
//
// "Endast en verifierad subscription_activated från betalningssystemet får … göra receptinformationen synlig för patienten"
// — the READY states need the release, never the registration alone.

export const TREATMENT_START_STATES = [
  // No published positive decision: none yet, or a negative one. Point 13:
  // a negative decision never enters the subscription or prescription chain.
  "NO_POSITIVE_DECISION",
  "MEDICAL_DECISION_APPROVED",
  "AWAITING_SUBSCRIPTION",
  "SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION",
  // TES-1150 — the doctor has registered the prescription; the subscription
  // is not verified yet, so nothing of it is the patient's. Admin's
  // "Recept utfärdat – inväntar abonnemangsaktivering".
  "PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION",
  "PRESCRIPTION_READY",
  "TREATMENT_START_READY",
] as const;
export const treatmentStartStateSchema = z.enum(TREATMENT_START_STATES);
export type TreatmentStartState = z.infer<typeof treatmentStartStateSchema>;

export type TreatmentStartFacts = {
  /**
   * The newest PUBLISHED treatment decision: the CP-09 notice for a new
   * treatment, the decided transfer review for a prescription transfer.
   * Never a draft, a proposal or a meeting outcome that was not published.
   */
  decision: "none" | "approved" | "declined";
  /** A subscription link has been minted and sent (a stored row). */
  subscriptionLinkSent: boolean;
  /**
   * The subscription is verified entitled: the Stripe status the webhook
   * stored (TES-591's single entitlement rule), never a checkout redirect.
   */
  subscriptionActive: boolean;
  /**
   * TES-1150 — the doctor has registered the prescription in admin
   * ("Bekräfta receptstatus"): `prescriptionRegisteredAt` on the first
   * cycle. A cycle activated before TES-1150 counts too (its activation was
   * the doctor's confirmation).
   */
  prescriptionRegistered: boolean;
  /**
   * The registered prescription has been released to the patient: the first
   * cycle has been activated (`activatedAt`). Since TES-1150 only a verified
   * subscription releases it (at the webhook, or at the registration when
   * she was already entitled). Patients activated before TES-1069 have it
   * too and keep their state (Simon 2026-10-02).
   */
  released: boolean;
  /** The confirmed cycle's `medicationPickupRequired` (CP-12 variant A). */
  medicationPickupRequired: boolean | null;
  /** Her "Jag har hämtat ut läkemedlet" for that cycle. */
  medicationPickedUp: boolean;
  /** `first_dose_reported` has been recorded. */
  firstDoseReported: boolean;
};

export function treatmentStartState(
  facts: TreatmentStartFacts,
): TreatmentStartState {
  if (facts.decision !== "approved") return "NO_POSITIVE_DECISION";
  if (facts.released) {
    // Point 12: with a pickup, "Hämta ut ditt läkemedel" comes before
    // "Registrera din första dos". A reported first dose means the medicine
    // is in hand whatever the pickup check-off says (the check-off is a
    // self-report and never required for the dose, medication-pickup.ts).
    const pickupOutstanding =
      facts.medicationPickupRequired === true &&
      !facts.medicationPickedUp &&
      !facts.firstDoseReported;
    return pickupOutstanding ? "PRESCRIPTION_READY" : "TREATMENT_START_READY";
  }
  // Registered, not released. With a verified subscription too, this is
  // only ever seen between the two writes of one release (which runs in the
  // webhook's or the registration's own transaction): still not hers.
  if (facts.prescriptionRegistered) {
    return "PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION";
  }
  if (facts.subscriptionActive) {
    return "SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION";
  }
  if (facts.subscriptionLinkSent) return "AWAITING_SUBSCRIPTION";
  return "MEDICAL_DECISION_APPROVED";
}

// TES-1070 S1 — the doctor's prescription step. TES-1150: it no longer
// publishes the treatment (the verified subscription does), so the button
// says what it does, in Emelie's words: "bekräfta receptstatusen i admin".
export const CONFIRM_PRESCRIPTION_CTA = "Bekräfta receptstatus";

// TES-1150 — the admin status once the doctor's session is complete and the
// subscription is not yet verified. Emelie's words, verbatim.
export const PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL =
  "Recept utfärdat – inväntar abonnemangsaktivering";

// Only the two choices that start a treatment. "Recept kan inte utfärdas ännu"
// (`not_yet`) is NOT offered at this step: whether it should be is Emelie's
// open question 3 in TES-1069, and until she answers, the step can only
// confirm a prescription, never record its absence.
export const CONFIRM_PRESCRIPTION_CHOICES = [
  "new_issued",
  "existing_valid",
] as const;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date_required");

// The prescription choice of this step. TES-1080: the prescription note's
// text is compiled from it too, so the note and the confirmation take the
// same shape.
export const confirmPrescriptionChoiceSchema = z
  .object({
    choice: z.enum(CONFIRM_PRESCRIPTION_CHOICES),
    existingValidUntil: isoDate.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.existingValidUntil && v.choice !== "existing_valid") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["existingValidUntil"],
        message: "valid_until_only_with_existing_prescription",
      });
    }
  });
export type ConfirmPrescriptionChoice = z.infer<
  typeof confirmPrescriptionChoiceSchema
>;

// TES-1150 — the prescription choice only. The separate prescription note
// and its Webdoc tick (TES-1080) are gone: the decision's journal note,
// confirmed in Webdoc before the decision could be published, is the
// session's one note ("Kirill ska därefter inte behöva … skriva en ny journalanteckning, eller lämna en ny Webdoc-kvittens").
// An older admin bundle that still sends `journalDone` / `noteRevisionId`
// has them stripped here.
export const confirmPrescriptionSchema = z.object({
  prescription: confirmPrescriptionChoiceSchema,
});
export type ConfirmPrescription = z.infer<typeof confirmPrescriptionSchema>;

// What the admin reads to decide whether to offer the button, and what the
// doctor confirms: the planned order on the pending first cycle and the phase
// the decision chose.
export const treatmentStartViewSchema = z.object({
  state: treatmentStartStateSchema,
  patientGroup: patientGroupSchema,
  clinicalPhase: clinicalPhaseSchema.nullable(),
  subscriptionStatus: z.string(),
  plannedOrder: z
    .object({
      cycleId: z.string(),
      medication: z.string().nullable(),
      dose: z.string(),
      frequency: z.string().nullable(),
      duration: z.string().nullable(),
    })
    .nullable(),
  // TES-1150 — the doctor's registration while it waits for the
  // subscription. Null before it and once released.
  registration: z
    .object({
      choice: z.enum(CONFIRM_PRESCRIPTION_CHOICES),
      existingValidUntil: z.string().nullable(),
      registeredAt: z.string(),
    })
    .nullable()
    .optional(),
});
export type TreatmentStartView = z.infer<typeof treatmentStartViewSchema>;

export const confirmPrescriptionResultSchema = z.object({
  // True when the prescription had already been registered (or released):
  // a second submit changes nothing.
  alreadyConfirmed: z.boolean(),
  // TES-1150 — whether the registration was released at once (she was
  // already entitled) rather than left waiting for the subscription.
  released: z.boolean().optional(),
  view: treatmentStartViewSchema,
});
export type ConfirmPrescriptionResult = z.infer<
  typeof confirmPrescriptionResultSchema
>;
